import { useState, useEffect, useRef, useMemo, useCallback, type MutableRefObject } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  ClipboardList,
  Filter,
  LayoutGrid,
  List,
  Download,
  Kanban,
  Eye,
  EyeOff,
  LayoutDashboard,
  Mail,
  UserPlus,
  GitBranch,
  Ban,
  User,
  Pencil,
  Upload,
  Briefcase,
  Building2,
} from 'lucide-react';
import { candidatePortraitUrl } from '@modules/role-fit/candidatePortraitUrl';
import { BulkComposePanel } from '@shared/components/BulkComposePanel';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions, canViewAssigneeActivity } from '@core/auth/usePermissions';
import { queryKeys } from '@core/query/queryKeys';
import { isApplicationStatusConflictError } from '@core/supabase/applicationStatusConflict';
import { applicationService } from '@core/supabase/services/applicationService';
import { useApplicationsOrgRealtime } from './useApplicationsOrgRealtime';
import { clientService } from '@core/supabase/services/clientService';
import { orgService } from '@core/supabase/services/orgService';
import {
  Application,
  ApplicationStatus,
  Client,
  REJECTION_REASON_CODES,
  REJECTION_REASON_LABELS,
  type RejectionReasonCode,
} from '@core/types';
import {
  ALL_STATUSES,
  PIPELINE_STATUS_LABEL,
  buildKanbanColumns,
  parseOrgPipelineOrder,
} from '@core/constants/pipelineStages';
import { ListOperationsDropdown, type ListExportFmt } from '@shared/components/ui/ListOperationsDropdown';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { useToast } from '@shared/hooks/useToast';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { AssistantIntake } from '@modules/agent/components/AssistantIntake';
import { INTAKE_CONFIGS } from '@modules/agent/intake.config';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { ApplicationStatusChip } from '@shared/components/ui/StatusChip';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { SortableTh, EnrichColumnTh } from '@shared/components/ui/SortableTableHeader';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { RowHoverActions } from '@shared/components/ui/RowHoverActions';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import { Modal } from '@shared/components/ui/Modal';
import {
  KANBAN_BACKLOG_PRESET,
  loadKanbanHiddenStatuses,
  saveKanbanHiddenStatuses,
} from './kanbanBoardPrefs';
import { ApplicationPipelinePreviewBody } from './ApplicationPipelineDrawer';
import { ListPreviewDrawer } from '@shared/components/ui/ListPreviewDrawer';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';

const STATUS_LABEL = PIPELINE_STATUS_LABEL;

/** Accent color per pipeline stage — used for column header tints */
const STAGE_COLOR: Record<ApplicationStatus, string> = {
  submitted:               '#3B82F6',
  viewed_by_client:        '#8B80F9',
  shortlisted:             '#06B6D4',
  interview_1_scheduled:   '#F59E0B',
  interview_1_conducted:   '#F97316',
  interview_2_scheduled:   '#10B981',
  interview_2_conducted:   '#059669',
  offer_extended:          '#22C55E',
  offer_accepted:          '#16A34A',
  offer_rejected:          '#EF4444',
  placed:                  '#6366F1',
  rejected:                '#F43F5E',
  no_show:                 '#94A3B8',
  on_hold:                 '#F59E0B',
  closed:                  '#64748B',
};

const STATUSES_REQUIRING_REJECTION: ApplicationStatus[] = ['rejected', 'offer_rejected'];

function statusNeedsRejectionReason(s: ApplicationStatus): boolean {
  return STATUSES_REQUIRING_REJECTION.includes(s);
}

function ApplicationsListSkeleton() {
  return (
    <div className="card overflow-hidden p-0">
      <div className="space-y-0 divide-y divide-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)] p-4" aria-busy="true" aria-label="Loading applications">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="py-3">
            <div className="h-4 w-2/3 max-w-md animate-pulse rounded vscode-bg-tertiary" />
            <div className="mt-2 h-3 w-1/3 max-w-xs animate-pulse rounded vscode-bg-tertiary" />
          </div>
        ))}
      </div>
    </div>
  );
}

function pipelineLockIsActive(app: Application): boolean {
  const exp = app.pipeline_lock_expires_at;
  if (!exp) return false;
  return new Date(exp).getTime() > Date.now();
}

/** Another org user holds a non-expired pipeline lease on this application. */
function isPipelineLockedByOther(app: Application, myOrgUserId: string | null | undefined): boolean {
  if (!myOrgUserId || !pipelineLockIsActive(app) || !app.pipeline_lock_org_user_id) return false;
  return app.pipeline_lock_org_user_id !== myOrgUserId;
}

function ApplicationKanbanCard({
  app,
  draggingId,
  lastDragEndAt,
  onDragStart,
  onDragEnd,
  onOpen,
  pendingRejectionReason,
  lockedByOther,
  lockMessage,
}: {
  app: Application;
  draggingId: string | null;
  lastDragEndAt: MutableRefObject<number>;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onOpen: (id: string) => void;
  pendingRejectionReason?: boolean;
  lockedByOther?: boolean;
  lockMessage?: string;
}) {
  const pic = candidatePortraitUrl(app.candidate_id, 48);
  const c = app.candidate;
  const canDrag = !lockedByOther;
  const jobTitle = app.job?.title ?? '—';
  const clientName = app.client?.name ?? '—';
  const candidateTitle = c?.current_title ?? '';

  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => {
        if (!canDrag) {
          e.preventDefault();
          return;
        }
        onDragStart(app.id);
        e.dataTransfer.setData('applicationId', app.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        lastDragEndAt.current = Date.now();
        onDragEnd();
      }}
      onClick={() => {
        if (Date.now() - lastDragEndAt.current < 400) return;
        onOpen(app.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(app.id);
        }
      }}
      role="button"
      tabIndex={0}
      title={lockedByOther && lockMessage ? lockMessage : undefined}
      className={`rounded-xl border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] p-3 transition-all text-left outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
        canDrag
          ? 'cursor-pointer hover:border-emerald-500/40 hover:bg-[var(--vscode-bg-hover)] hover:shadow-md'
          : 'cursor-default opacity-90'
      } ${
        draggingId === app.id ? 'opacity-60 ring-2 ring-brand-500/60 scale-[1.02]' : ''
      } ${lockedByOther ? 'border-amber-700/40 bg-amber-950/20' : ''}`}
    >
      {lockedByOther && lockMessage ? (
        <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400/95 mb-2 leading-snug rounded bg-amber-500/10 px-2 py-1">{lockMessage}</p>
      ) : null}
      {pendingRejectionReason ? (
        <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400/95 mb-2 leading-snug rounded bg-amber-500/10 px-2 py-1">
          Rejection reason required before Apply
        </p>
      ) : null}

      {/* Candidate info */}
      <div className="flex items-start gap-3 min-w-0">
        <img
          src={pic}
          alt=""
          className="h-12 w-12 shrink-0 rounded-xl object-cover border vscode-border bg-vscode-bg-tertiary"
          width={48}
          height={48}
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold vscode-text-primary text-[13px] leading-tight truncate">{c?.full_name ?? '—'}</p>
          {candidateTitle ? (
            <p className="text-[11px] vscode-text-secondary truncate mt-0.5">{candidateTitle}</p>
          ) : null}
        </div>
      </div>

      {/* Job & Client info */}
      <div className="mt-3 space-y-1.5 border-t vscode-border pt-3">
        <div className="flex items-center gap-2">
          <Briefcase className="h-3 w-3 vscode-text-tertiary shrink-0" />
          <p className="text-[11px] vscode-text-primary truncate font-medium">{jobTitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-3 w-3 vscode-text-tertiary shrink-0" />
          <p className="text-[11px] vscode-text-secondary truncate">{clientName}</p>
        </div>
      </div>

      {/* Assignee if available */}
      {app.assigned_user?.display_name ? (
        <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-2 py-1">
          <div className="h-4 w-4 rounded-full bg-emerald-500/20 flex items-center justify-center text-[8px] font-semibold text-emerald-600 dark:text-emerald-400">
            {app.assigned_user.display_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <span className="text-[10px] text-emerald-700 dark:text-emerald-400 truncate">{app.assigned_user.display_name}</span>
        </div>
      ) : null}
    </div>
  );
}

export function ApplicationsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | ''>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [assignedFilter, setAssignedFilter] = useState<string>('');
  const [clients, setClients] = useState<Client[]>([]);
  const [assignees, setAssignees] = useState<{ id: string; display_name: string }[]>([]);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('list');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ApplicationStatus | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Partial<Record<string, ApplicationStatus>>>({});
  const [kanbanRejections, setKanbanRejections] = useState<
    Record<string, { code: RejectionReasonCode; text: string }>
  >({});
  const [rejectionModalApp, setRejectionModalApp] = useState<Application | null>(null);
  const [rejectionDraft, setRejectionDraft] = useState<{ code: RejectionReasonCode; text: string }>({
    code: 'not_qualified',
    text: '',
  });
  const [rejectionModalError, setRejectionModalError] = useState('');
  const [kanbanApplyError, setKanbanApplyError] = useState<string | null>(null);
  const [applyingKanban, setApplyingKanban] = useState(false);
  const [pipelineRemoteHint, setPipelineRemoteHint] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [kanbanColumnOrder, setKanbanColumnOrder] = useState<ApplicationStatus[]>(ALL_STATUSES);
  const [kanbanHiddenStatuses, setKanbanHiddenStatuses] = useState<Set<ApplicationStatus>>(() => new Set());
  const [kanbanConfigOpen, setKanbanConfigOpen] = useState(false);
  const [kanbanConfigDraftHidden, setKanbanConfigDraftHidden] = useState<Set<ApplicationStatus>>(() => new Set());
  type AppListSortCol = 'candidate' | 'title' | 'job' | 'client' | 'status' | 'assignee' | 'created_at';
  const [listSortBy, setListSortBy] = useState<AppListSortCol>('created_at');
  const [listSortAsc, setListSortAsc] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [drawerAppId, setDrawerAppId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; appId: string } | null>(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState<ApplicationStatus | ''>('');
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState<string>('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectCode, setBulkRejectCode] = useState<RejectionReasonCode>('not_qualified');
  const [bulkRejectText, setBulkRejectText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pipelineToast, setPipelineToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const lastShiftRef = useRef<{ index: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const kanbanRejectionsRef = useRef(kanbanRejections);
  const lastKanbanDragEndAt = useRef(0);
  const hadStatusInUrl = useRef(false);
  const hadAssignedInUrl = useRef(false);
  const hadClientInUrl = useRef(false);
  const navigate = useNavigate();

  const candidateId = searchParams.get('candidateId') ?? undefined;
  const jobId = searchParams.get('jobId') ?? undefined;
  const clientIdFromUrl = searchParams.get('clientId') ?? undefined;
  const assignedToFromUrl = searchParams.get('assignedTo') ?? undefined;
  const statusFromUrl = searchParams.get('status') as ApplicationStatus | null;

  const orgId = user?.orgId;

  const listParams = useMemo(
    () => ({
      candidateId,
      jobId,
      status: viewMode === 'list' && statusFilter ? statusFilter : undefined,
      clientId: clientFilter || undefined,
      assignedTo: assignedFilter || undefined,
      limit: 2000 as const,
    }),
    [candidateId, jobId, viewMode, statusFilter, clientFilter, assignedFilter],
  );

  const applicationsQuery = useQuery({
    queryKey: orgId ? queryKeys.applications.list(orgId, listParams) : ['applications', '__none'],
    queryFn: () => applicationService.list(orgId!, listParams),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });

  const applications = applicationsQuery.data ?? [];
  const loading = Boolean(orgId && applicationsQuery.isPending);
  const fetchError =
    applicationsQuery.error instanceof Error
      ? applicationsQuery.error.message
      : applicationsQuery.error
        ? 'Could not load applications'
        : null;

  const pushToast = useCallback((kind: 'ok' | 'err', text: string) => {
    setPipelineToast({ kind, text });
  }, []);

  useEffect(() => {
    if (!pipelineToast) return;
    const id = window.setTimeout(() => setPipelineToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [pipelineToast]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (contextMenuRef.current?.contains(t)) return;
      setContextMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [contextMenu]);

  /**
   * When the URL drops ?clientId / ?assignedTo / ?status after you had them, clear matching UI filters.
   * We only clear on that transition so list-view dropdown choices aren’t wiped on every remount.
   */
  useEffect(() => {
    if (statusFromUrl && ALL_STATUSES.includes(statusFromUrl)) {
      setStatusFilter(statusFromUrl);
      hadStatusInUrl.current = true;
    } else if (hadStatusInUrl.current) {
      setStatusFilter('');
      hadStatusInUrl.current = false;
    }
  }, [statusFromUrl]);

  useEffect(() => {
    if (assignedToFromUrl) {
      setAssignedFilter(assignedToFromUrl);
      hadAssignedInUrl.current = true;
    } else if (hadAssignedInUrl.current) {
      setAssignedFilter('');
      hadAssignedInUrl.current = false;
    }
  }, [assignedToFromUrl]);

  useEffect(() => {
    if (clientIdFromUrl) {
      setClientFilter(clientIdFromUrl);
      hadClientInUrl.current = true;
    } else if (hadClientInUrl.current) {
      setClientFilter('');
      hadClientInUrl.current = false;
    }
  }, [clientIdFromUrl]);

  useEffect(() => {
    if (!user?.orgId) return;
    clientService.list(user.orgId).then((r) => setClients(Array.isArray(r) ? r : r.data));
    orgService.getTeamMembers(user.orgId).then((m) => setAssignees(m.map((u) => ({ id: u.id, display_name: u.display_name ?? u.email }))));
    orgService.getOrg(user.orgId).then((o) => {
      const parsed = o?.pipeline_stage_order != null ? parseOrgPipelineOrder(o.pipeline_stage_order) : null;
      setKanbanColumnOrder(parsed ?? ALL_STATUSES);
    });
  }, [user?.orgId]);

  const kanbanColumns = useMemo(() => buildKanbanColumns(kanbanColumnOrder), [kanbanColumnOrder]);

  const visibleKanbanColumns = useMemo(() => {
    const vis = kanbanColumns.filter((c) => !kanbanHiddenStatuses.has(c.id));
    return vis.length > 0 ? vis : kanbanColumns;
  }, [kanbanColumns, kanbanHiddenStatuses]);

  useEffect(() => {
    if (!user?.orgId || !user?.id) return;
    setKanbanHiddenStatuses(loadKanbanHiddenStatuses(user.orgId, user.id));
  }, [user?.orgId, user?.id]);

  useEffect(() => { setPage(1); }, [statusFilter, search, clientFilter, assignedFilter]);

  useEffect(() => {
    setStatusOverrides({});
  }, [statusFilter, clientFilter, assignedFilter, candidateId, jobId, search]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? applications.filter((a) => {
        const name = (a.candidate?.full_name ?? '').toLowerCase();
        const title = (a.job?.title ?? '').toLowerCase();
        const clientName = (a.client?.name ?? '').toLowerCase();
        return name.includes(q) || title.includes(q) || clientName.includes(q);
      })
    : applications;

  const scopeDescription = [
    candidateId && 'candidate',
    jobId && 'job',
    clientIdFromUrl && 'client',
    assignedToFromUrl && 'assignee',
  ]
    .filter(Boolean)
    .join(', ');
  const sortedForListTable = useMemo(() => {
    const arr = [...filtered];
    const dir = listSortAsc ? 1 : -1;
    const strSort = (a: Application, b: Application, get: (x: Application) => string) =>
      dir * get(a).localeCompare(get(b), undefined, { sensitivity: 'base' });
    const dateSort = (a: Application, b: Application) =>
      dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    arr.sort((a, b) => {
      switch (listSortBy) {
        case 'candidate':
          return strSort(a, b, (x) => x.candidate?.full_name ?? '');
        case 'title':
          return strSort(a, b, (x) => x.candidate?.current_title ?? '');
        case 'job':
          return strSort(a, b, (x) => x.job?.title ?? '');
        case 'client':
          return strSort(a, b, (x) => x.client?.name ?? '');
        case 'status':
          return strSort(a, b, (x) => x.status);
        case 'assignee':
          return strSort(a, b, (x) => x.assigned_user?.display_name ?? '');
        case 'created_at':
        default:
          return dateSort(a, b);
      }
    });
    return arr;
  }, [filtered, listSortBy, listSortAsc]);

  const totalItems = sortedForListTable.length;
  const paginated = sortedForListTable.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (viewMode !== 'list' || paginated.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
      const rowEls = Array.from(
        document.querySelectorAll<HTMLTableRowElement>('table.ggj-data-table tbody tr'),
      );
      if (rowEls.length === 0) return;
      let idx = rowEls.findIndex((r) => r === document.activeElement);
      if (idx < 0) idx = 0;
      else if (e.key === 'ArrowDown') idx = Math.min(rowEls.length - 1, idx + 1);
      else idx = Math.max(0, idx - 1);
      rowEls[idx]?.focus();
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewMode, paginated.length, page]);

  const handleListSort = (col: AppListSortCol) => {
    if (listSortBy === col) {
      setListSortAsc((x) => !x);
    } else {
      setListSortBy(col);
      setListSortAsc(col === 'created_at' ? false : true);
    }
    setPage(1);
  };

  const runOperationsExport = useCallback(
    (fmt: ListExportFmt, scope: 'selection' | 'all') => {
      if (fmt === 'csv') {
        const rows =
          scope === 'all' ? filtered : filtered.filter((a) => selectedIds.has(a.id));
        if (rows.length === 0) {
          showToast({ title: scope === 'selection' ? 'Could not export this selection.' : 'No applications to export.', kind: 'error' });
          return;
        }
        const csv = applicationService.exportCsv(rows);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `applications-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      listExportNotImplemented('Applications', fmt, scope);
    },
    [filtered, selectedIds],
  );

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = paginated.length > 0 && selectedIds.size > 0 && selectedIds.size < paginated.length;
    }
  }, [paginated.length, selectedIds.size]);

  const byStatus = useMemo(() => {
    const acc = {} as Record<ApplicationStatus, Application[]>;
    for (const s of ALL_STATUSES) acc[s] = [];
    for (const app of filtered) {
      const serverStatus = applications.find((a) => a.id === app.id)?.status ?? app.status;
      const effective = statusOverrides[app.id] ?? serverStatus;
      acc[effective].push(app);
    }
    return acc;
  }, [filtered, applications, statusOverrides]);

  const pendingKanbanCount = Object.keys(statusOverrides).length;
  const pendingKanbanRef = useRef(pendingKanbanCount);
  pendingKanbanRef.current = pendingKanbanCount;

  useApplicationsOrgRealtime(orgId ?? undefined, queryClient, {
    onRemoteChange: () => {
      if (pendingKanbanRef.current > 0) setPipelineRemoteHint(true);
    },
  });

  const handleKanbanDrop = useCallback(
    async (targetStatus: ApplicationStatus, app: Application) => {
      const serverStatus = applications.find((a) => a.id === app.id)?.status ?? app.status;
      setKanbanApplyError(null);

      if (targetStatus === serverStatus) {
        if (user?.orgUserId) {
          try {
            await applicationService.releasePipelineLock(app.id, user.orgUserId);
          } catch (e) {
            console.warn('[kanban] release pipeline lock', e);
          }
        }
        setStatusOverrides((prev) => {
          const next = { ...prev };
          delete next[app.id];
          return next;
        });
        setKanbanRejections((prev) => {
          const next = { ...prev };
          delete next[app.id];
          return next;
        });
        setDragOverColumn(null);
        setDraggingId(null);
        return;
      }

      if (!user?.orgUserId) {
        setKanbanApplyError(
          'Your profile is not linked to this organisation — pipeline lock is unavailable. Ask an admin to fix your account, then refresh.',
        );
        setDragOverColumn(null);
        setDraggingId(null);
        return;
      }

      const lockRes = await applicationService.acquirePipelineLock(app.id, user.orgUserId);
      if (!lockRes.ok) {
        if (lockRes.reason === 'locked') {
          setKanbanApplyError(
            `${lockRes.lockedByDisplayName ?? 'Another recruiter'} is editing this application on the pipeline. Try again when they finish or after the lock expires (~2 minutes).`,
          );
        } else {
          setKanbanApplyError(
            lockRes.reason === 'no_org_user'
              ? 'Could not acquire pipeline lock (missing org user).'
              : 'Could not acquire pipeline lock.',
          );
        }
        setDragOverColumn(null);
        setDraggingId(null);
        return;
      }

      setStatusOverrides((prev) => ({ ...prev, [app.id]: targetStatus }));
      if (statusNeedsRejectionReason(targetStatus)) {
        setRejectionModalError('');
        const pending = kanbanRejectionsRef.current[app.id];
        setRejectionDraft(
          pending ?? {
            code: (app.rejection_reason_code as RejectionReasonCode) ?? 'not_qualified',
            text: app.rejection_reason_text ?? '',
          },
        );
        setRejectionModalApp(app);
      } else {
        setKanbanRejections((prev) => {
          const next = { ...prev };
          delete next[app.id];
          return next;
        });
      }
      setDragOverColumn(null);
      setDraggingId(null);
    },
    [applications, user?.orgUserId],
  );

  const pendingOverrideIdsKey = useMemo(
    () =>
      Object.keys(statusOverrides)
        .sort()
        .join(','),
    [statusOverrides],
  );

  useEffect(() => {
    if (!user?.orgUserId || !pendingOverrideIdsKey) return;
    const ids = pendingOverrideIdsKey.split(',').filter(Boolean);
    if (ids.length === 0) return;

    const extend = () => {
      void Promise.all(
        ids.map((applicationId) =>
          applicationService.acquirePipelineLock(applicationId, user.orgUserId!, 120).catch((e) => {
            console.warn('[kanban] pipeline lock heartbeat', e);
          }),
        ),
      );
    };

    const iv = window.setInterval(extend, 45_000);
    return () => window.clearInterval(iv);
  }, [user?.orgUserId, pendingOverrideIdsKey]);

  const handleApplyKanban = useCallback(async () => {
    if (!user?.orgId || pendingKanbanCount === 0) return;
    for (const [id, status] of Object.entries(statusOverrides)) {
      if (!status || !statusNeedsRejectionReason(status)) continue;
      const r = kanbanRejections[id];
      if (!r?.code) {
        setKanbanApplyError('Every move to a Rejected column needs a reason. Open the card’s column and pick a reason, or drag the card again.');
        return;
      }
      if (r.code === 'other' && !r.text.trim()) {
        setKanbanApplyError('Rejection reason “Other” needs a short note. Edit the card in a Rejected column.');
        return;
      }
    }
    setApplyingKanban(true);
    setKanbanApplyError(null);
    setPipelineRemoteHint(false);
    try {
      for (const [id, status] of Object.entries(statusOverrides)) {
        if (!status) continue;
        const row = applications.find((a) => a.id === id);
        const concurrency = { expectedUpdatedAt: row?.updated_at };
        if (statusNeedsRejectionReason(status)) {
          const r = kanbanRejections[id];
          if (!r?.code) continue;
          await applicationService.updateStatus(id, status, undefined, r.code, r.text.trim() || null, concurrency);
        } else {
          await applicationService.updateStatus(id, status, undefined, undefined, undefined, concurrency);
        }
      }
      setStatusOverrides({});
      setKanbanRejections({});
      await queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(user.orgId) });
      pushToast('ok', 'Pipeline changes saved.');
    } catch (err) {
      if (isApplicationStatusConflictError(err)) {
        setKanbanApplyError(err.message);
        setStatusOverrides({});
        setKanbanRejections({});
        await queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(user.orgId) });
        pushToast('err', err.message);
      } else {
        console.error('Failed to apply pipeline changes:', err);
        const msg = err instanceof Error ? err.message : 'Could not save pipeline changes';
        setKanbanApplyError(msg);
        pushToast('err', msg);
      }
    } finally {
      setApplyingKanban(false);
    }
  }, [user?.orgId, pendingKanbanCount, statusOverrides, kanbanRejections, queryClient, applications, pushToast]);

  const handleDiscardKanban = useCallback(() => {
    setStatusOverrides((prev) => {
      const ids = Object.keys(prev);
      if (user?.orgUserId && ids.length > 0) {
        void Promise.all(
          ids.map((applicationId) =>
            applicationService.releasePipelineLock(applicationId, user.orgUserId!).catch(() => {}),
          ),
        );
      }
      return {};
    });
    setKanbanRejections({});
    setKanbanApplyError(null);
    setRejectionModalApp(null);
    setPipelineRemoteHint(false);
  }, [user?.orgUserId]);

  const runBulkMove = useCallback(async () => {
    if (!user?.orgId || selectedIds.size === 0 || !bulkMoveTarget) return;
    if (statusNeedsRejectionReason(bulkMoveTarget)) {
      pushToast('err', 'Use Reject for rejection outcomes.');
      return;
    }
    setBulkBusy(true);
    try {
      await applicationService.bulkUpdateStatus(user.orgId, Array.from(selectedIds), bulkMoveTarget);
      pushToast('ok', `Updated ${selectedIds.size} application(s).`);
      setSelectedIds(new Set());
      await queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(user.orgId) });
      setBulkMoveOpen(false);
      setBulkMoveTarget('');
    } catch (e) {
      pushToast('err', e instanceof Error ? e.message : 'Bulk move failed');
    } finally {
      setBulkBusy(false);
    }
  }, [user?.orgId, selectedIds, bulkMoveTarget, queryClient, pushToast]);

  const runBulkAssign = useCallback(async () => {
    if (!user?.orgId || selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await applicationService.assign(id, bulkAssignTo || null);
      }
      pushToast('ok', `Assignee updated for ${ids.length} application(s).`);
      setSelectedIds(new Set());
      await queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(user.orgId) });
      setBulkAssignOpen(false);
      setBulkAssignTo('');
    } catch (e) {
      pushToast('err', e instanceof Error ? e.message : 'Bulk assign failed');
    } finally {
      setBulkBusy(false);
    }
  }, [user?.orgId, selectedIds, bulkAssignTo, queryClient, pushToast]);

  const runBulkReject = useCallback(async () => {
    if (!user?.orgId || selectedIds.size === 0) return;
    if (bulkRejectCode === 'other' && !bulkRejectText.trim()) {
      pushToast('err', 'Add a short note when the reason is Other.');
      return;
    }
    setBulkBusy(true);
    try {
      await applicationService.bulkUpdateStatus(user.orgId, Array.from(selectedIds), 'rejected', {
        rejectionReasonCode: bulkRejectCode,
        rejectionReasonText: bulkRejectText.trim() || null,
      });
      pushToast('ok', `Rejected ${selectedIds.size} application(s).`);
      setSelectedIds(new Set());
      await queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(user.orgId) });
      setBulkRejectOpen(false);
      setBulkRejectText('');
    } catch (e) {
      pushToast('err', e instanceof Error ? e.message : 'Bulk reject failed');
    } finally {
      setBulkBusy(false);
    }
  }, [user?.orgId, selectedIds, bulkRejectCode, bulkRejectText, queryClient, pushToast]);

  const openBulkEmail = useCallback(() => {
    const first = Array.from(selectedIds)[0];
    if (!first) {
      pushToast('err', 'Select at least one row.');
      return;
    }
    navigate(
      `/emails/compose?applicationId=${encodeURIComponent(first)}&returnTo=${encodeURIComponent('/applications')}`,
    );
  }, [selectedIds, navigate, pushToast]);

  // Single consolidated toolbar strip (search + filters + view toggle + Reports +
  // New) — replaces the previous 3 stacked rows (duplicate breadcrumb title,
  // view-toggle row, filter row) per user feedback. The shell Topbar already
  // shows the "Applications" crumb, so no PageHeader is rendered here.
  const viewToggle = (
    <div className="flex overflow-hidden rounded-lg border vscode-border">
      <button
        type="button"
        title="Table (default)"
        onClick={() => setViewMode('list')}
        className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
          viewMode === 'list'
            ? 'vscode-bg-elevated vscode-text-primary'
            : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary'
        }`}
      >
        <List className="h-4 w-4" />
      </button>
      <button
        type="button"
        title="Kanban board (optional)"
        onClick={() => setViewMode('kanban')}
        className={`border-l vscode-border px-2.5 py-1.5 text-xs font-medium transition-colors ${
          viewMode === 'kanban'
            ? 'vscode-bg-elevated vscode-text-primary'
            : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary'
        }`}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
    </div>
  );

  const reportsDropdown = (
    <ListOperationsDropdown
      objectHistoryHref={objectHistoryUrl('applications')}
      onExport={(fmt, scope) => void runOperationsExport(fmt, scope)}
      extraActions={[
        ...(viewMode === 'kanban'
          ? [
              {
                id: 'configure-board',
                label: 'Configure board',
                icon: <Kanban className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => {
                  setKanbanConfigDraftHidden(new Set(kanbanHiddenStatuses));
                  setKanbanConfigOpen(true);
                },
              },
            ]
          : []),
        {
          id: 'export-csv',
          label: 'Export visible as CSV',
          icon: <Download className="vscode-text-secondary" strokeWidth={2} />,
          onClick: () => {
            const csv = applicationService.exportCsv(filtered);
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `applications-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          },
        },
        {
          id: 'import',
          label: 'Import',
          icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
          onClick: () =>
            showToast({ title: 'Bulk application import is not supported. Create applications from a job or candidate record.', kind: 'error' }),
        },
        {
          id: 'manage-rights',
          label: 'Manage Rights',
          icon: <User className="vscode-text-secondary" strokeWidth={2} />,
          onClick: () => setManageRightsOpen(true),
        },
      ]}
    />
  );

  const applicationsIntakeCfg = INTAKE_CONFIGS.find((c) => c.route === '/applications') ?? null;
  const noFiltersApplied =
    !search.trim() && !statusFilter && !clientFilter && !assignedFilter && !candidateId && !jobId;

  // Phase 8 T-22 §27.B — intake surface when /applications has no filter applied.
  if (
    !applicationsQuery.isPending &&
    applications.length === 0 &&
    noFiltersApplied &&
    applicationsIntakeCfg
  ) {
    return (
      <AssistantIntake
        greeting={applicationsIntakeCfg.greeting}
        placeholder={applicationsIntakeCfg.placeholder}
        scopePill={applicationsIntakeCfg.scopePill}
        suggestions={applicationsIntakeCfg.suggestions}
        beta={applicationsIntakeCfg.beta}
        entityPlural={applicationsIntakeCfg.entityPlural}
        firstName={(user?.name ?? user?.email ?? 'there').split(/[ @]/)[0] || 'there'}
        gradient="intake"
        onSubmit={(text) => setSearch(text)}
      />
    );
  }

  return (
    <div className="w-full min-w-0 max-w-none">
      {toast}
      <SyncStatusBadge className="mb-3" />
      {/* One strip: search + filters + view toggle + Reports + New. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
          <input
            className="input pl-9"
            placeholder="Search candidate, job, client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search applications"
          />
        </div>
        <Filter className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden />
        <select
          className="input w-auto disabled:opacity-50"
          value={statusFilter}
          disabled={viewMode === 'kanban'}
          title={viewMode === 'kanban' ? 'Status filter applies in list view. Board always loads the full pipeline.' : undefined}
          onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus | '')}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          aria-label="Filter by client"
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={assignedFilter}
          onChange={(e) => setAssignedFilter(e.target.value)}
          aria-label="Filter by assignee"
        >
          <option value="">Assigned to anyone</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name}
            </option>
          ))}
        </select>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {viewToggle}
          {reportsDropdown}
          <Link
            to="/applications/new"
            className="btn-primary inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
            New application
          </Link>
        </div>
      </div>

      {fetchError && (
        <div className="mb-4 rounded-xl border border-red-800/80 bg-red-950/35 px-4 py-3 text-sm">
          <p className="font-medium text-red-200">Couldn’t load applications</p>
          <p className="mt-1 text-red-300/90">{fetchError}</p>
          <p className="mt-2 text-xs text-red-400/80">
            Check the browser console and Supabase RLS. If this persists, try signing out and back in.
          </p>
        </div>
      )}

      {!fetchError && !loading && scopeDescription && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2 text-xs text-gray-400">
          <span>
            Showing a filtered slice ({scopeDescription}). Clear the URL to see the full pipeline.
          </span>
          <Link to="/applications" className="text-brand-400 hover:underline font-medium">
            Show all applications
          </Link>
        </div>
      )}

      {(statusFilter || clientFilter || assignedFilter) && (
        <div className="mb-4 flex flex-wrap items-center gap-2" aria-label="Active filters">
          <span className="text-xs vscode-text-tertiary">Filters:</span>
          {statusFilter ? (
            <button
              type="button"
              className="rounded-full border vscode-border bg-[color-mix(in_srgb,var(--vscode-primary)_12%,transparent)] px-3 py-1 text-xs font-medium vscode-text-primary"
              onClick={() => setStatusFilter('')}
            >
              {STATUS_LABEL[statusFilter]} <span className="opacity-70">×</span>
            </button>
          ) : null}
          {clientFilter ? (
            <button
              type="button"
              className="rounded-full border vscode-border bg-[color-mix(in_srgb,var(--vscode-primary)_12%,transparent)] px-3 py-1 text-xs font-medium vscode-text-primary"
              onClick={() => setClientFilter('')}
            >
              {clients.find((c) => c.id === clientFilter)?.name ?? 'Client'} <span className="opacity-70">×</span>
            </button>
          ) : null}
          {assignedFilter ? (
            <button
              type="button"
              className="rounded-full border vscode-border bg-[color-mix(in_srgb,var(--vscode-primary)_12%,transparent)] px-3 py-1 text-xs font-medium vscode-text-primary"
              onClick={() => setAssignedFilter('')}
            >
              {assignees.find((a) => a.id === assignedFilter)?.display_name ?? 'Assignee'}{' '}
              <span className="opacity-70">×</span>
            </button>
          ) : null}
        </div>
      )}

      {viewMode === 'list' && selectedIds.size > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border vscode-border vscode-bg-elevated px-4 py-3"
          role="region"
          aria-label="Bulk actions"
        >
          <span className="text-sm font-medium vscode-text-primary">{selectedIds.size} selected</span>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs"
            disabled={selectedIds.size !== 1}
            onClick={() => {
              const id = Array.from(selectedIds)[0];
              if (id) navigate(`/applications/${id}`);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs"
            disabled={bulkBusy}
            onClick={() => {
              setBulkMoveTarget('');
              setBulkMoveOpen(true);
            }}
          >
            <GitBranch className="h-3.5 w-3.5" />
            Move stage
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs"
            disabled={bulkBusy}
            onClick={() => {
              setBulkAssignTo('');
              setBulkAssignOpen(true);
            }}
          >
            <UserPlus className="h-3.5 w-3.5" />
            Assign
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs"
            disabled={bulkBusy}
            onClick={() => void openBulkEmail()}
          >
            <Mail className="h-3.5 w-3.5" />
            Send email
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs border-red-800/50 text-red-300 hover:bg-red-950/40"
            disabled={bulkBusy}
            onClick={() => {
              setBulkRejectCode('not_qualified');
              setBulkRejectText('');
              setBulkRejectOpen(true);
            }}
          >
            <Ban className="h-3.5 w-3.5" />
            Reject
          </button>
          <button
            type="button"
            className="ml-auto text-xs vscode-text-link hover:underline"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      {viewMode === 'kanban' && !loading && (
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-2 flex-wrap">
          <span>
            Board loads up to 2,000 applications across all stages.{' '}
            <span className="text-gray-400">Scroll horizontally</span> to reach every column (
            {visibleKanbanColumns.length}
            {visibleKanbanColumns.length !== kanbanColumns.length ? ` of ${kanbanColumns.length}` : ''} visible).
          </span>
        </p>
      )}

      {viewMode === 'kanban' && pipelineRemoteHint && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-800/70 bg-sky-950/35 px-4 py-3 text-sm text-sky-100">
          <span>
            The pipeline was updated remotely (another tab or teammate). Data is refreshing. If you had unsaved drags,{' '}
            <strong className="font-medium">Apply</strong> checks for conflicts, or use <strong className="font-medium">Discard</strong>.
          </span>
          <button type="button" className="text-xs underline shrink-0" onClick={() => setPipelineRemoteHint(false)}>
            Dismiss
          </button>
        </div>
      )}

      {viewMode === 'kanban' && kanbanApplyError && (
        <div className="mb-4 rounded-lg border border-red-800/80 bg-red-950/35 px-4 py-3 text-sm text-red-200">
          {kanbanApplyError}
        </div>
      )}

      {viewMode === 'kanban' && pendingKanbanCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-600/50 bg-amber-950/35 px-4 py-3">
          <span className="text-sm text-amber-100">
            {pendingKanbanCount} status change{pendingKanbanCount === 1 ? '' : 's'} pending (not saved yet)
            {'. '}
            Moves to <strong className="font-medium">Rejected</strong> columns need a reason before Apply.
          </span>
          <button
            type="button"
            className="btn-primary text-sm py-1.5"
            disabled={applyingKanban}
            onClick={handleApplyKanban}
          >
            {applyingKanban ? 'Applying…' : 'Apply changes'}
          </button>
          <button
            type="button"
            className="btn-secondary text-sm py-1.5"
            disabled={applyingKanban}
            onClick={handleDiscardKanban}
          >
            Discard
          </button>
        </div>
      )}

      {loading && viewMode === 'list' ? (
        <ApplicationsListSkeleton />
      ) : loading ? (
        <FullPageSpinner />
      ) : viewMode === 'kanban' ? (
        <>
          {applications.length === 0 && (
            <div className="mb-3 rounded-xl border border-gray-800 bg-gray-950/50 px-4 py-3 text-sm text-gray-300">
              <p className="font-medium text-gray-200">Pipeline is empty</p>
              <p className="mt-1 text-gray-500">
                Create an application to see cards here. Stages follow your visibility settings ({visibleKanbanColumns.length} column
                {visibleKanbanColumns.length !== 1 ? 's' : ''} shown).
              </p>
              <Link to="/applications/new" className="btn-primary mt-3 inline-flex text-sm">
                <Plus className="w-4 h-4" /> New application
              </Link>
            </div>
          )}
          {applications.length > 0 && filtered.length === 0 && search.trim() && (
            <div className="mb-3 rounded-xl border border-amber-600/40 bg-amber-950/25 px-4 py-2 text-sm text-amber-100">
              No applications match &ldquo;{search.trim()}&rdquo;. Clear search to see all cards.
            </div>
          )}
          <div className="w-full min-w-0 overflow-x-auto overflow-y-visible pb-4 -mx-1 px-1 scroll-smooth">
            <div className="flex gap-4 min-w-max pb-1">
              {visibleKanbanColumns.map((col) => {
                const apps = byStatus[col.id] ?? [];
                const isOver = dragOverColumn === col.id;
                return (
                  <div
                    key={col.id}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverColumn(col.id);
                    }}
                    onDragLeave={() => setDragOverColumn(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      const id = e.dataTransfer.getData('applicationId');
                      const app = filtered.find((a) => a.id === id);
                      if (app) void handleKanbanDrop(col.id, app);
                      setDragOverColumn(null);
                    }}
                    className={`flex-shrink-0 w-72 rounded-lg transition-colors min-h-[400px] border ${
                      isOver
                        ? 'border-emerald-500 bg-emerald-500/5'
                        : 'border-[var(--vscode-border-primary)] bg-transparent'
                    }`}
                  >
                    {/* Stage header — soft pastel tint (lighter than prior bold strip) */}
                    <div
                      className="px-3 py-2 sticky top-0 backdrop-blur-sm rounded-t-[0.65rem] z-[1] flex items-center justify-between border-b"
                      style={{
                        background: `linear-gradient(180deg, ${STAGE_COLOR[col.id]}12 0%, ${STAGE_COLOR[col.id]}06 100%)`,
                        borderBottomColor: `${STAGE_COLOR[col.id]}22`,
                        borderTop: `2px solid ${STAGE_COLOR[col.id]}30`,
                        borderLeft: `1px solid ${STAGE_COLOR[col.id]}18`,
                        borderRight: `1px solid ${STAGE_COLOR[col.id]}12`,
                      }}
                    >
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: STAGE_COLOR[col.id], opacity: 0.92 }}
                      >
                        {col.label}
                      </span>
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                        style={{
                          background: `${STAGE_COLOR[col.id]}14`,
                          color: STAGE_COLOR[col.id],
                        }}
                      >
                        {apps.length}
                      </span>
                    </div>
                    <div className="p-2 space-y-3 overflow-y-auto max-h-[calc(100vh-280px)]">
                      {apps.map((app) => {
                        const serverSt = applications.find((a) => a.id === app.id)?.status ?? app.status;
                        const effective = statusOverrides[app.id] ?? serverSt;
                        const pendingReason =
                          Boolean(statusOverrides[app.id]) &&
                          statusNeedsRejectionReason(effective) &&
                          !kanbanRejections[app.id];
                        const live = applications.find((a) => a.id === app.id) ?? app;
                        const lockedByOther = isPipelineLockedByOther(live, user?.orgUserId);
                        const lockMessage = lockedByOther
                          ? `${live.pipeline_lock_holder?.display_name ?? live.pipeline_lock_holder?.email ?? 'Another recruiter'} is editing this card`
                          : undefined;
                        return (
                          <ApplicationKanbanCard
                            key={app.id}
                            app={app}
                            draggingId={draggingId}
                            lastDragEndAt={lastKanbanDragEndAt}
                            onDragStart={setDraggingId}
                            onDragEnd={() => setDraggingId(null)}
                            onOpen={(id) => navigate(`/applications/${id}`)}
                            pendingRejectionReason={pendingReason}
                            lockedByOther={lockedByOther}
                            lockMessage={lockMessage}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={applications.length > 0 && search.trim() ? 'No matching applications' : 'No applications yet'}
          description={
            applications.length > 0 && search.trim()
              ? 'Try another search or clear the search box.'
              : 'Submit a candidate for a job to start tracking your pipeline.'
          }
          action={
            <Link to="/applications/new" className="btn-primary">
              <Plus className="w-4 h-4" /> New application
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="ggj-data-table">
            <thead className="sticky top-0 z-20 bg-[var(--vscode-bg-secondary)] shadow-[inset_0_-1px_0_0_var(--vscode-border-primary)]">
              <tr className="border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    ref={headerCheckRef}
                    checked={paginated.length > 0 && selectedIds.size === paginated.length}
                    onChange={() => {
                      lastShiftRef.current = null;
                      if (selectedIds.size === paginated.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(paginated.map((a) => a.id)));
                    }}
                    className="rounded border-gray-600"
                  />
                </th>
                <SortableTh
                  label="Candidate"
                  column="candidate"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left"
                />
                <SortableTh
                  label="Title"
                  column="title"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left hidden sm:table-cell"
                />
                <SortableTh
                  label="Job"
                  column="job"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left hidden lg:table-cell"
                />
                <SortableTh
                  label="Client"
                  column="client"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left hidden lg:table-cell"
                />
                <SortableTh
                  label="Status"
                  column="status"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left"
                />
                <SortableTh
                  label="Assigned To"
                  column="assignee"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left hidden md:table-cell"
                />
                <SortableTh
                  label="Created When"
                  column="created_at"
                  current={listSortBy}
                  asc={listSortAsc}
                  onSort={handleListSort}
                  className="text-left hidden lg:table-cell whitespace-nowrap"
                />
                <EnrichColumnTh />
              </tr>
            </thead>
            <tbody>
              {paginated.map((app, rowIndex) => {
                const absIndex = (page - 1) * pageSize + rowIndex;
                return (
                <tr
                  key={app.id}
                  tabIndex={0}
                  className={`group border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_40%,transparent)] outline-none transition-colors hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_65%,transparent)] focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
                    selectedIds.has(app.id) ? 'bg-[color-mix(in_srgb,var(--vscode-primary)_10%,transparent)]' : ''
                  }`}
                  onClick={() => setDrawerAppId(app.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDrawerAppId(app.id);
                    }
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      setDrawerAppId(app.id);
                    }
                    if (e.key === 'ArrowLeft' && drawerAppId) {
                      e.preventDefault();
                      setDrawerAppId(null);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ x: e.clientX, y: e.clientY, appId: app.id });
                  }}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.id)}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (
                          e.nativeEvent instanceof MouseEvent &&
                          e.nativeEvent.shiftKey &&
                          lastShiftRef.current != null
                        ) {
                          const from = Math.min(lastShiftRef.current.index, absIndex);
                          const to = Math.max(lastShiftRef.current.index, absIndex);
                          for (let i = from; i <= to; i++) {
                            const row = sortedForListTable[i];
                            if (row) next.add(row.id);
                          }
                          setSelectedIds(next);
                        } else {
                          if (next.has(app.id)) next.delete(app.id);
                          else next.add(app.id);
                          setSelectedIds(next);
                        }
                        lastShiftRef.current = { index: absIndex };
                      }}
                      className="rounded border-gray-600"
                    />
                  </td>
                  <td className="px-3 py-0">
                    <div className="flex items-center gap-2">
                      <TableAvatar name={app.candidate?.full_name ?? '?'} size={20} />
                      <p className="text-[13px] font-medium vscode-text-primary truncate">{app.candidate?.full_name ?? '—'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-sm vscode-text-secondary">
                    {app.candidate?.current_title?.trim() ? app.candidate.current_title : '—'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-sm vscode-text-secondary">{app.job?.title ?? '—'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-sm vscode-text-secondary">{app.client?.name ?? '—'}</td>
                  <td className="px-4 py-3">
                    <ApplicationStatusChip status={app.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 hidden md:table-cell">
                    {app.assigned_to &&
                    canViewAssigneeActivity(perms, user?.orgUserId, app.assigned_to) ? (
                      <Link
                        to={`/activities?tab=feed&actorId=${encodeURIComponent(app.assigned_to)}&actorName=${encodeURIComponent(
                          app.assigned_user?.display_name ?? 'Recruiter'
                        )}`}
                        className="text-brand-400 hover:text-brand-300 hover:underline"
                        title="Open activity feed for this assignee (RBAC: self, or senior+ / admin)"
                      >
                        {app.assigned_user?.display_name ?? 'Assignee'}
                      </Link>
                    ) : app.assigned_user?.display_name ? (
                      <span title="Activity link requires permission or assign to yourself">
                        {app.assigned_user.display_name}
                      </span>
                    ) : (
                      <span className="text-gray-600">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell tabular-nums whitespace-nowrap">
                    {formatCreatedWhen(app.created_at)}
                  </td>
                  <RowHoverActions />
                </tr>
              );
              })}
            </tbody>
          </table>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            entityLabel="applications"
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </div>
      )}

      {orgId && drawerAppId ? (
        <ListPreviewDrawer
          open
          onClose={() => setDrawerAppId(null)}
          typeLabel="Application"
          title={applications.find((a) => a.id === drawerAppId)?.candidate?.full_name ?? 'Application'}
          subtitle={applications.find((a) => a.id === drawerAppId)?.job?.title}
          openFullHref={`/applications/${drawerAppId}`}
        >
          <ApplicationPipelinePreviewBody
            orgId={orgId}
            applicationId={drawerAppId}
            listApp={applications.find((a) => a.id === drawerAppId) ?? null}
            hideOpenFullLink
            onClose={() => setDrawerAppId(null)}
          />
        </ListPreviewDrawer>
      ) : null}

      {contextMenu ? (
        <div
          ref={contextMenuRef}
          className="fixed z-[160] min-w-[200px] rounded-lg border vscode-border vscode-bg-elevated py-1 shadow-xl"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              setDrawerAppId(contextMenu.appId);
              setContextMenu(null);
            }}
          >
            Open summary panel
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              navigate(`/applications/${contextMenu.appId}`);
              setContextMenu(null);
            }}
          >
            Open in full page
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              navigate(
                `/emails/compose?applicationId=${encodeURIComponent(contextMenu.appId)}&returnTo=${encodeURIComponent('/applications')}`,
              );
              setContextMenu(null);
            }}
          >
            Send email
          </button>
        </div>
      ) : null}

      <Modal
        open={bulkMoveOpen}
        onClose={() => !bulkBusy && setBulkMoveOpen(false)}
        title="Move stage (bulk)"
        subtitle={`${selectedIds.size} application(s) selected`}
        size="md"
      >
        <div className="space-y-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium vscode-text-tertiary">Target stage</span>
            <select
              className="input w-full"
              value={bulkMoveTarget}
              onChange={(e) => setBulkMoveTarget((e.target.value || '') as ApplicationStatus | '')}
            >
              <option value="">Choose stage…</option>
              {ALL_STATUSES.filter((s) => !statusNeedsRejectionReason(s)).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs vscode-text-tertiary">Rejection outcomes use the Reject bulk action (with confirmation).</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" disabled={bulkBusy} onClick={() => setBulkMoveOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn-primary" disabled={bulkBusy || !bulkMoveTarget} onClick={() => void runBulkMove()}>
              {bulkBusy ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkAssignOpen}
        onClose={() => !bulkBusy && setBulkAssignOpen(false)}
        title="Assign (bulk)"
        subtitle={`${selectedIds.size} application(s) selected`}
        size="md"
      >
        <div className="space-y-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium vscode-text-tertiary">Assignee</span>
            <select className="input w-full" value={bulkAssignTo} onChange={(e) => setBulkAssignTo(e.target.value)}>
              <option value="">Unassigned</option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" disabled={bulkBusy} onClick={() => setBulkAssignOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn-primary" disabled={bulkBusy} onClick={() => void runBulkAssign()}>
              {bulkBusy ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkRejectOpen}
        onClose={() => !bulkBusy && setBulkRejectOpen(false)}
        title="Reject applications?"
        subtitle={`This will mark ${selectedIds.size} application(s) as rejected.`}
        variant="danger"
        size="md"
      >
        <div className="space-y-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium vscode-text-tertiary">Reason</span>
            <select
              className="input w-full"
              value={bulkRejectCode}
              onChange={(e) => setBulkRejectCode(e.target.value as RejectionReasonCode)}
            >
              {REJECTION_REASON_CODES.map((c) => (
                <option key={c} value={c}>
                  {REJECTION_REASON_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium vscode-text-tertiary">Note {bulkRejectCode === 'other' ? '(required)' : ''}</span>
            <textarea className="input w-full min-h-[72px]" value={bulkRejectText} onChange={(e) => setBulkRejectText(e.target.value)} />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" disabled={bulkBusy} onClick={() => setBulkRejectOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn-primary" disabled={bulkBusy} onClick={() => void runBulkReject()}>
              {bulkBusy ? 'Rejecting…' : 'Confirm reject'}
            </button>
          </div>
        </div>
      </Modal>

      {pipelineToast ? (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-[200] max-w-sm rounded-lg border px-4 py-3 text-sm shadow-lg ${
            pipelineToast.kind === 'ok'
              ? 'border-emerald-800/60 bg-emerald-950/90 text-emerald-100'
              : 'border-red-800/60 bg-red-950/90 text-red-100'
          }`}
        >
          {pipelineToast.text}
        </div>
      ) : null}

      <Modal
        open={kanbanConfigOpen}
        onClose={() => setKanbanConfigOpen(false)}
        title="Configure Kanban Board"
        subtitle="Visible stages — saved per user in this browser"
        icon={<Kanban className="w-4 h-4" />}
        size="lg"
      >
        <div className="space-y-5 text-sm max-h-[min(74vh,560px)] overflow-y-auto pr-1">
          {/* Info banner */}
          <div
            className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-xs"
            style={{ background: 'var(--vscode-editor-inactiveSelectionBackground)', color: 'var(--vscode-descriptionForeground)' }}
          >
            <LayoutDashboard className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
            <span>
              Preferences are saved in this browser only. Hiding a stage removes its column — applications still in that stage
              won&apos;t appear on the board until shown again or changed in <strong>list view</strong>.
            </span>
          </div>

          {/* Visible Stages */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" style={{ color: '#8B80F9' }} />
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-foreground)' }}>
                  Visible Stages
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors"
                  style={{ background: 'var(--vscode-button-secondaryBackground)', color: 'var(--vscode-button-secondaryForeground)' }}
                  onClick={() => setKanbanConfigDraftHidden(new Set())}
                >
                  Show all
                </button>
                <button
                  type="button"
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors"
                  style={{ background: 'var(--vscode-button-secondaryBackground)', color: 'var(--vscode-button-secondaryForeground)' }}
                  onClick={() =>
                    setKanbanConfigDraftHidden((prev) => {
                      const next = new Set(prev);
                      for (const s of KANBAN_BACKLOG_PRESET) next.add(s);
                      return next;
                    })
                  }
                >
                  <EyeOff className="w-3 h-3 inline mr-1" />
                  Hide backlog
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {kanbanColumnOrder.map((s) => {
                const visible = !kanbanConfigDraftHidden.has(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setKanbanConfigDraftHidden((prev) => {
                        const next = new Set(prev);
                        if (next.has(s)) next.delete(s);
                        else next.add(s);
                        return next;
                      });
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all select-none"
                    style={
                      visible
                        ? {
                            background: 'rgba(139,128,249,0.15)',
                            borderColor: '#8B80F9',
                            color: '#8B80F9',
                          }
                        : {
                            background: 'var(--vscode-editor-background)',
                            borderColor: 'var(--vscode-widget-border)',
                            color: 'var(--vscode-descriptionForeground)',
                            opacity: 0.6,
                          }
                    }
                  >
                    {visible ? (
                      <Eye className="w-3 h-3 shrink-0" />
                    ) : (
                      <EyeOff className="w-3 h-3 shrink-0" />
                    )}
                    {STATUS_LABEL[s]}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] mt-2" style={{ color: 'var(--vscode-descriptionForeground)' }}>
              Backlog preset hides: Submitted, Viewed, On hold
            </p>
          </div>

          {/* Footer */}
          <div
            className="flex justify-end gap-2 pt-3"
            style={{ borderTop: '1px solid var(--vscode-widget-border)' }}
          >
            <button type="button" className="btn-secondary" onClick={() => setKanbanConfigOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (!user?.orgId || !user?.id) return;
                const wouldHideAll = kanbanColumnOrder.every((s) => kanbanConfigDraftHidden.has(s));
                if (wouldHideAll) {
                  showToast({ title: 'Keep at least one pipeline stage visible on the board.', kind: 'error' });
                  return;
                }
                setKanbanHiddenStatuses(new Set(kanbanConfigDraftHidden));
                saveKanbanHiddenStatuses(user.orgId, user.id, kanbanConfigDraftHidden);
                setKanbanConfigOpen(false);
              }}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      {rejectionModalApp ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kanban-rejection-title"
        >
          <div
            className="w-full max-w-md rounded-xl border border-gray-700 bg-gray-900 p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="kanban-rejection-title" className="text-white font-semibold text-base mb-1">
              Rejection reason
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              {rejectionModalApp.candidate?.full_name ?? 'Candidate'} · {rejectionModalApp.job?.title ?? 'Job'}
            </p>
            <label className="label">Reason code</label>
            <select
              className="input w-full mb-3"
              value={rejectionDraft.code}
              onChange={(e) =>
                setRejectionDraft((d) => ({ ...d, code: e.target.value as RejectionReasonCode }))
              }
            >
              {REJECTION_REASON_CODES.map((c) => (
                <option key={c} value={c}>
                  {REJECTION_REASON_LABELS[c]}
                </option>
              ))}
            </select>
            <label className="label">Note {rejectionDraft.code === 'other' ? '(required)' : '(optional)'}</label>
            <textarea
              className="input w-full min-h-[80px] py-2"
              placeholder="Visible on the application record"
              value={rejectionDraft.text}
              onChange={(e) => setRejectionDraft((d) => ({ ...d, text: e.target.value }))}
            />
            {rejectionModalError ? (
              <p className="mt-2 text-sm text-red-400">{rejectionModalError}</p>
            ) : null}
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                className="btn-secondary flex-1"
                onClick={() => {
                  const id = rejectionModalApp.id;
                  setStatusOverrides((prev) => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                  });
                  setKanbanRejections((prev) => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                  });
                  setRejectionModalApp(null);
                  setRejectionModalError('');
                }}
              >
                Cancel move
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => {
                  if (rejectionDraft.code === 'other' && !rejectionDraft.text.trim()) {
                    setRejectionModalError('Add a short note when the reason is “Other”.');
                    return;
                  }
                  setKanbanRejections((prev) => ({
                    ...prev,
                    [rejectionModalApp.id]: {
                      code: rejectionDraft.code,
                      text: rejectionDraft.text.trim(),
                    },
                  }));
                  setRejectionModalApp(null);
                  setRejectionModalError('');
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={orgId ?? null}
        entityKey="applications"
        subtitle="Per-user access for the Applications / pipeline list — stored locally per browser."
      />

      {/* §28.B — Bulk compose panel: slides up when ≥ 2 rows are selected */}
      {selectedIds.size >= 2 ? (
        <BulkComposePanel
          recipients={Array.from(selectedIds).map((id) => {
            const app = applications.find((a) => a.id === id);
            return {
              id,
              name: app?.candidate?.full_name ?? '',
              email: null,
            };
          })}
          onClose={() => setSelectedIds(new Set())}
        />
      ) : null}
    </div>
  );
}
